import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell, Check, Trash2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  listNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification,
} from "@/lib/intel/notifications.functions";
import { subscribeToTable } from "@/lib/realtime";
import { useAuth } from "@/lib/auth/AuthContext";

export function NotificationsPopover() {
  const list = useServerFn(listNotifications);
  const markRead = useServerFn(markNotificationRead);
  const markAll = useServerFn(markAllNotificationsRead);
  const del = useServerFn(deleteNotification);
  const qc = useQueryClient();
  const { user } = useAuth();

  const q = useQuery({
    queryKey: ["notifications"],
    queryFn: () => list(),
    enabled: !!user,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    return subscribeToTable(
      `notifications-${uid}`,
      { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${uid}` },
      () => { qc.invalidateQueries({ queryKey: ["notifications"] }); },
    );
  }, [user?.id, qc]);

  const readM = useMutation({
    mutationFn: (id: string) => markRead({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const allM = useMutation({
    mutationFn: () => markAll(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const delM = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const items = q.data ?? [];
  const unread = items.filter((n) => !n.readAt).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label="Notifications"
          className="relative rounded-md border border-panel-border bg-background/40 p-1.5 text-muted-foreground hover:text-foreground"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 min-w-[16px] h-[16px] rounded-full bg-danger px-1 text-[9px] font-mono font-bold text-white grid place-items-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={8} className="w-[min(360px,calc(100vw-1.5rem))] p-0 border-panel-border bg-panel/95 backdrop-blur">
        <div className="flex items-center justify-between border-b border-panel-border px-3 py-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Notifications</div>
          <button
            onClick={() => allM.mutate()}
            disabled={!unread}
            className="text-[10px] font-mono uppercase text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            Mark all read
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {q.isLoading && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
          {!q.isLoading && !items.length && (
            <div className="p-6 text-center text-xs text-muted-foreground">No notifications yet.</div>
          )}
          {items.map((n) => {
            const tone =
              n.severity === "critical" ? "border-l-danger"
              : n.severity === "warning" ? "border-l-amber"
              : n.severity === "success" ? "border-l-success"
              : "border-l-cyan";
            const inner = (
              <div className={`border-l-2 ${tone} px-3 py-2 hover:bg-background/40 ${!n.readAt ? "bg-background/30" : ""}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{n.title}</div>
                    {n.body && <div className="text-[11px] text-muted-foreground line-clamp-2">{n.body}</div>}
                    <div className="mt-1 font-mono text-[9px] text-muted-foreground">
                      {new Date(n.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {!n.readAt && (
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); readM.mutate(n.id); }}
                        className="text-muted-foreground hover:text-success" title="Mark read">
                        <Check className="h-3 w-3" />
                      </button>
                    )}
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); delM.mutate(n.id); }}
                      className="text-muted-foreground hover:text-danger" title="Delete">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
            return n.href ? (
              <Link key={n.id} to={n.href as any} onClick={() => !n.readAt && readM.mutate(n.id)} className="block">
                {inner}
              </Link>
            ) : (
              <div key={n.id}>{inner}</div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}